import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ComponentProps } from 'react';

import Link from '@vs-shop/image-link/Link';

import { Button, type ButtonProps } from '@vs-shop/ui/button';

import { clsxm } from '@vs-shop/utils';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

/**
 * The control under a listing: the pages it can be read at, and the button that
 * appends the next one to what is already shown. Flat named parts a tenant
 * arranges in its own JSX, each painting itself.
 *
 *   <Pagination aria-label={navLabel}>
 *     <PaginationPrevious href={hrefFor(currentPage - 1)} aria-label={previousLabel} onClick={…} />
 *
 *     {range.map((item) =>
 *       item.type === 'ellipsis' ? (
 *         <PaginationEllipsis key={…}>...</PaginationEllipsis>
 *       ) : (
 *         <PaginationPage
 *           key={item.page}
 *           page={item.page}
 *           lastPage={lastPage}
 *           smallScreenPagesCount={smallScreenPagesCount}
 *           href={hrefFor(item.page)}
 *           aria-current={item.page === currentPage ? 'page' : undefined}
 *           aria-label={pageLabel(item.page)}
 *           onClick={…}
 *         >
 *           {item.page}
 *         </PaginationPage>
 *       )
 *     )}
 *
 *     <PaginationNext href={hrefFor(currentPage + 1)} aria-label={nextLabel} onClick={…} />
 *   </Pagination>
 *
 * **Which page is the current one arrives as `aria-current` and as nothing
 * else.** It is already the attribute that names the current item in a set, it
 * is what a screen reader announces, and it rides the native prop spread — so
 * the part takes no `isActive` prop that could disagree with it, and the
 * current page's fill is selected from the DOM the same way `@vs-shop/ui`'s
 * controls select theirs. One statement of the state, in the place that has to
 * carry it regardless.
 *
 * **Which pages exist is the composition's.** The range — where the ellipses
 * fall, how many numbers surround the current one — is arithmetic over two
 * numbers, and it stays at the call site with the rest of the tenant's paging.
 * Nothing here computes it, and no part is told how long the run is.
 *
 * **Every page item is a real anchor.** A listing's pages are addressable URLs
 * that a crawler must be able to follow and a shopper must be able to
 * middle-click, so the click handler that swaps the grid in place is layered
 * ON an anchor rather than replacing one. The hrefs are built tenant-side,
 * where the route and its query belong, and arrive ready.
 *
 * No `variant`: pagination paints the same under every listing. What differs
 * between listings is how many pages there are and whether a load-more button
 * is composed at all — both of them the composition's.
 *
 * No strings: the region's label, each page's label and the load-more copy are
 * a tenant's language and arrive as props and children. The chevrons are not
 * copy — they are the part's own mark for "back" and "on", hidden from
 * assistive tech, which reads the labels instead.
 *
 * Server-first. Nothing here holds state; a tenant's own client component
 * supplies the handlers.
 */

export type PaginationProps = ComponentProps<'nav'>;

/**
 * The region the pages sit in. A `<nav>` because a run of page links IS a way
 * around the listing; the label naming it is the tenant's, since only a tenant
 * knows what its shoppers call this.
 *
 * `items-center` and nothing else: the row does not distribute its children,
 * because a run composed without its leading chevron would then place the
 * numbers wherever the remaining spacing left them. Each part anchors itself.
 *
 * Where the row SITS under the grid, and whether it is centred there, is page
 * assembly and stays in tenant JSX; nothing here renders a container.
 */
const paginationClasses = 'pho-lg:gap-2 flex items-center gap-1';

/** The pagination region. */
export const Pagination = ({ className, ...props }: PaginationProps) => (
  <nav data-slot="pagination" className={clsxm(paginationClasses, className)} {...props} />
);

/**
 * The step controls at either end of the run. A ring on hover rather than a
 * fill, so that pointing at a step never reads as the current page — which is
 * the one thing in this row that a fill means.
 */
const paginationStepClasses = 'hover:ring-foreground/20 rounded-md px-3 py-1.5 hover:ring';

/** The chevron inside a step control: the part's own mark, never announced. */
const paginationStepIconClasses = 'size-4';

/**
 * A step control takes no children — it renders its own chevron — so its
 * accessible name has to arrive as a label. Whether one is composed at all is
 * the tenant's: there is no previous page to point at from the first.
 */
export type PaginationStepProps = Omit<VsLinkProps, 'children'>;

/** The step back one page. */
export const PaginationPrevious = ({ className, ...props }: PaginationStepProps) => (
  <Link data-slot="pagination-prev" className={clsxm(paginationStepClasses, className)} {...props}>
    <ChevronLeft
      data-slot="pagination-prev-icon"
      aria-hidden
      className={paginationStepIconClasses}
    />
  </Link>
);

/** The step on one page. */
export const PaginationNext = ({ className, ...props }: PaginationStepProps) => (
  <Link data-slot="pagination-next" className={clsxm(paginationStepClasses, className)} {...props}>
    <ChevronRight
      data-slot="pagination-next-icon"
      aria-hidden
      className={paginationStepIconClasses}
    />
  </Link>
);

/** The gap in the run, padded to the width of a step so the row keeps its rhythm. */
const paginationEllipsisClasses = 'px-3 py-1.5';

export type PaginationEllipsisProps = ComponentProps<'span'>;

/**
 * The stretch of pages the run skips. What it is drawn with is the tenant's —
 * three periods, a single glyph — because that is typography rather than
 * anatomy, so it arrives as children.
 */
export const PaginationEllipsis = ({ className, ...props }: PaginationEllipsisProps) => (
  <span
    data-slot="pagination-ellipsis"
    className={clsxm(paginationEllipsisClasses, className)}
    {...props}
  />
);

export type PaginationPageClassParameters = {
  /** The page this item leads to. */
  readonly page: number;
  /** The last page of the listing, which is never held back — it is the far end of the run. */
  readonly lastPage: number;
  /**
   * How many leading pages survive on a phone. The number is the TENANT's — how
   * many numbers fit follows from that brand's type and spacing — so it arrives
   * as a prop and is never a constant here.
   */
  readonly smallScreenPagesCount: number;
};

/**
 * Whether the narrow screen holds this page back.
 *
 * A function rather than a `cva`: the arms are not an appearance a caller picks
 * but a comparison of the page's number against the tenant's cap, and a page
 * inside the cap emits no class at all rather than the false arm of a pair.
 * Every class is still a literal, because Tailwind generates what it can read.
 *
 * The page is HIDDEN, not dropped: the run is a set of addressable URLs, and
 * one that is absent from the narrow document is absent from what a crawler
 * reading that document can follow.
 */
export const paginationPageClasses = ({
  page,
  lastPage,
  smallScreenPagesCount,
}: PaginationPageClassParameters): string =>
  clsxm({
    'pho-lg:block hidden': page > smallScreenPagesCount && page !== lastPage,
  });

/**
 * One page, as the link to it. The fill marks the page being read, selected off
 * `aria-current` so the mark and the announcement cannot disagree; hovering
 * rings instead, and the current page does not ring at all — it is already
 * filled, and a ring on top would read as a second state.
 */
const paginationPageLinkClasses = [
  'font-heading',
  'hover:ring-foreground/20 rounded-md px-5 py-1.5 text-xs hover:ring',
  'aria-[current=page]:bg-primary aria-[current=page]:hover:ring-0',
];

export type PaginationPageProps = VsLinkProps & PaginationPageClassParameters;

/** One page of the listing. */
export const PaginationPage = ({
  className,
  page,
  lastPage,
  smallScreenPagesCount,
  ...props
}: PaginationPageProps) => (
  <Link
    data-slot="pagination-page"
    className={clsxm(
      paginationPageClasses({ page, lastPage, smallScreenPagesCount }),
      paginationPageLinkClasses,
      className
    )}
    {...props}
  />
);

/**
 * The row the load-more button sits in, set off from the grid it extends.
 *
 * Whether it is composed AT ALL is the tenant's: a listing whose every page is
 * already rendered has nothing left to append, and a button that says otherwise
 * is a lie. That question is about how this tenant's paging works, not about
 * how the control looks, so it is answered at the call site.
 */
const paginationLoadMoreClasses = 'mt-8 flex w-full justify-center uppercase';

export type PaginationLoadMoreProps = ComponentProps<'div'>;

/** The load-more row. */
export const PaginationLoadMore = ({ className, ...props }: PaginationLoadMoreProps) => (
  <div
    data-slot="pagination-load-more"
    className={clsxm(paginationLoadMoreClasses, className)}
    {...props}
  />
);

/** The load-more button's own casing, over whatever the primitive paints. */
const paginationLoadMoreButtonClasses = 'uppercase';

export type PaginationLoadMoreButtonProps = ButtonProps;

/**
 * The control that appends the next page.
 *
 * A part rather than a `@vs-shop/ui` button composed at each call site: the
 * shape of "show me more of this listing" is the same decision in every tenant,
 * and leaving it out here would put it back into tenant JSX four times over. It
 * composes the primitive internally, so it inherits every appearance the
 * primitive has.
 *
 * What it says arrives as children, and whether it is waiting arrives as
 * `loading` — which the primitive already turns into the disabled state, so
 * nothing here restates it.
 *
 * It keeps the primitive's own `data-slot`: the element is a button before it
 * is this section's, and overriding the slot would take that away from every
 * selector — a test's, a tenant stylesheet's — that reads controls by it. The
 * row above is what identifies this one as the listing's.
 */
export const PaginationLoadMoreButton = ({
  className,
  ...props
}: PaginationLoadMoreButtonProps) => (
  <Button className={clsxm(paginationLoadMoreButtonClasses, className)} {...props} />
);
